import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Smartphone, Plus, AlertCircle, Lock } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '../../../components/ui'

interface AppConfig {
  id: number
  app_name: string
  bundle_id: string
  primary_color: string
  icon_1024_url: string
  is_active: boolean
}

interface OrganizationInfo {
  id: number
  name: string
  slug: string | null
}

export default function AppConfigPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [appName, setAppName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchConfig()
  }, [])

  async function fetchConfig() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/app-config'))
      setConfig(res.data.data ?? null)
      setOrganization(res.data.organization ?? null)
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        console.error('Erro ao buscar configuração do app', err)
      }
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }

  const hasSlug = Boolean(organization?.slug)
  const bundlePreview = organization?.slug
    ? `com.sispoli.${organization.slug.replace(/-/g, '')}`
    : ''

  function openCreateModal() {
    setAppName('')
    setModalOpen(true)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (creating || !appName.trim()) return

    setCreating(true)
    try {
      await axios.post(buildApiUrl('/v1/app-config'), { app_name: appName.trim() })
      toast('success', 'App criado')
      setModalOpen(false)
      await fetchConfig()
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao criar app'
      toast('error', message)
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/settings/general')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Voltar"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Mobile</h1>
          <p className="text-gray-500 mt-1">Configure o app da campanha para iOS e Android</p>
        </div>
      </div>

      {config ? (
        <Card className="p-6">
          <div className="flex items-start gap-4">
            {config.icon_1024_url ? (
              <img
                src={config.icon_1024_url}
                alt={config.app_name}
                className="w-16 h-16 rounded-2xl border border-gray-200 object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-brand-500" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{config.app_name}</h2>
              <p className="text-sm text-gray-500 font-mono mt-1">{config.bundle_id}</p>
              <div className="flex items-center gap-2 mt-3">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    config.is_active
                      ? 'bg-green-50 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      config.is_active ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  {config.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
            <Button onClick={() => navigate('/settings/app/edit')}>Editar</Button>
          </div>
        </Card>
      ) : !hasSlug ? (
        <Card>
          <EmptyState
            icon={<AlertCircle className="w-8 h-8" />}
            title="Organização sem slug"
            description="O slug da organização é necessário para gerar o Bundle ID do app. Configure o slug nos dados da organização antes de criar o app."
            action={
              <Button onClick={() => navigate('/settings/political')}>
                Configurar organização
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<Smartphone className="w-8 h-8" />}
            title="Nenhum app configurado"
            description="Configure o app mobile da sua campanha para publicar na App Store e Google Play com identidade visual própria."
            action={
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4" />
                Adicionar app
              </Button>
            }
          />
        </Card>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>Adicionar app</ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalBody className="space-y-4">
            <Input
              label="Nome do app *"
              value={appName}
              onChange={e => setAppName(e.target.value)}
              maxLength={30}
              required
              autoFocus
              placeholder="Ex: João Vereador"
              hint="Aparece abaixo do ícone. Máx 30 chars."
            />
            <Input
              label="Bundle ID"
              value={bundlePreview}
              readOnly
              disabled
              tabIndex={-1}
              iconRight={<Lock className="w-4 h-4" />}
              className="bg-gray-50 text-gray-500 cursor-not-allowed font-mono"
              hint="Gerado automaticamente do slug da organização. Não pode ser alterado."
            />
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={creating} disabled={!appName.trim()}>
              <Plus className="w-4 h-4 mr-2" />
              Criar app
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}
